// src/components/block/liveStreamManagement/RecordingSessionModal.tsx
// Boîte de dialogue du bouton Rec de la régie.
//
// Remplace l'ancienne fenêtre générique showConfirm() : elle nomme la session et,
// quand le bouton Multi-angle est actif, choisit les sources du deck à enregistrer.
//
// Le nom de fichier :
//   • champ laissé vide  → le nom d'enregistrement par défaut (buildRecordingFilename),
//     inchangé par rapport au comportement historique ;
//   • champ modifié      → le slug du nom saisi.
// En multi-angle, chaque source cochée reçoit en plus le slug de
// « {nom de la session} - {nom de la caméra} » (cf. buildAngleFilename).
//
// La durée restante affichée suit les cases cochées : chaque angle est un fichier de
// plus écrit en parallèle du programme, donc autant de quota consommé en même temps.
// Décocher une source rallonge le temps disponible, sous les yeux de l'opérateur.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Circle, VideoOff } from 'lucide-react';

import Toggle from '../../widget/inputs/Toggle';
import { formatBps } from '../../../backendServices/liveStreamManagment/api_bandwidth';
import { ANGLE_MAX_BITRATE } from '../../../backendServices/liveStreamManagment/api_recordingAngles';
import {
  RecordingAngle,
  buildRecordingAngles,
  buildFinalMixFilename,
  formatSecondsAsHms,
  remainingSecondsForStreams,
  sanitizeSessionName,
  slugifyRecordingName,
} from '../../../backendServices/liveStreamManagment/api_recording';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RecordingSessionSource {
  id    : string;
  label : string;
  /**
   * Path MediaMTX d'origine, quand la source en vient déjà. L'angle est alors enregistré
   * à la source : rien n'est republié depuis le navigateur.
   */
  mediamtxPath?: string;
}

export interface RecordingSessionPayload {
  /** Nom affiché de la session (celui saisi, ou le nom par défaut si le champ est vide). */
  sessionName : string;
  /** Nom de fichier de la session : slug du nom saisi, ou nom par défaut tel quel. */
  filename    : string;
  /** Multi-angle uniquement : un nom de fichier par source cochée. Vide sinon. */
  angles      : RecordingAngle[];
}

interface RecordingSessionModalProps {
  /** Bouton Multi-angle actif → titre différent et section « Sources à enregistrer ». */
  multiAngle    : boolean;
  /** Nom d'enregistrement par défaut, utilisé comme placeholder du champ. */
  defaultName   : string;
  /**
   * Quota d'enregistrement restant, en secondes, POUR UN SEUL flux (null = inconnu).
   * La fenêtre le ramène elle-même au nombre de fichiers que la prise va écrire.
   */
  remainingSec  : number | null;
  /** Sources présentes dans le deck (multi-angle uniquement). */
  deckSources   : RecordingSessionSource[];
  /**
   * Cette source livre-t-elle des images, là, maintenant ? Renvoie la raison quand non,
   * `null` quand tout va bien. Réinterrogée tant que la fenêtre est ouverte : une caméra
   * distante peut se rétablir, ou tomber, entre l'ouverture et le clic sur « Lancer ».
   */
  sourceHealth? : (sourceId: string) => Promise<string | null>;
  onCancel      : () => void;
  onStart       : (payload: RecordingSessionPayload) => void;
  onBuyStorage  : () => void;
}

// ─── Composant ────────────────────────────────────────────────────────────────

const RecordingSessionModal: React.FC<RecordingSessionModalProps> = ({
  multiAngle, defaultName, remainingSec, deckSources, sourceHealth, onCancel, onStart, onBuyStorage,
}) => {
  const [sessionName, setSessionName] = useState('');

  // On mémorise les sources DÉcochées, pas les cochées : toutes celles du deck sont
  // enregistrées par défaut (le cas courant du multi-angle), une source ajoutée au deck
  // pendant que la fenêtre est ouverte arrive donc cochée, et une source retirée cesse
  // simplement d'être listée — aucune resynchronisation sur `deckSources` n'est requise,
  // qui écraserait sinon les décochages de l'opérateur à chaque rendu du parent.
  const [disabledIds, setDisabledIds] = useState<Set<string>>(() => new Set());

  // ── Sources qui ne livrent rien ───────────────────────────────────────────
  //
  // Le 25/08/2026, une caméra a été proposée ici PRÉ-COCHÉE alors que son acquisition
  // venait d'échouer deux fois, 6 et 9 secondes plus tôt. L'opérateur ne pouvait pas le
  // savoir : la liste est bâtie sur les emplacements du deck, qui ne disent rien de
  // l'état des flux. La prise est partie sans elle, sans un mot.
  //
  // ⚠️ `sourceHealth` et `deckSources` sont tenus dans des refs, JAMAIS mis en
  // dépendance de l'effet : ce sont des valeurs recréées à chaque rendu du parent, et en
  // dépendre relancerait l'intervalle sans fin (cf. le figement du 2026-08-21 sur les
  // fonctions du contexte média).
  const [ailingSources, setAilingSources] = useState<Record<string, string>>({});

  const sourceHealthRef = useRef(sourceHealth);
  const deckSourcesRef  = useRef(deckSources);
  useEffect(() => { sourceHealthRef.current = sourceHealth; });
  useEffect(() => { deckSourcesRef.current  = deckSources;  });

  useEffect(() => {
    if (!multiAngle) return;
    let vivant = true;

    const releve = async () => {
      const check = sourceHealthRef.current;
      if (!check) return;

      const next: Record<string, string> = {};
      for (const source of deckSourcesRef.current) {
        // Une source enregistrée à la source ne passe pas par le navigateur : son état
        // ici ne dit rien de ce que le serveur enregistrera.
        if (source.mediamtxPath) continue;
        try {
          const raison = await check(source.id);
          if (raison) next[source.id] = raison;
        } catch { /* un relevé raté ne vaut pas un diagnostic */ }
      }
      if (!vivant) return;

      // Remplacer l'objet à chaque seconde re-rendrait la fenêtre pour rien.
      setAilingSources(prev => {
        const memeTaille = Object.keys(prev).length === Object.keys(next).length;
        if (memeTaille && Object.keys(next).every(id => prev[id] === next[id])) return prev;
        return next;
      });
    };

    releve();
    const timer = setInterval(releve, 1500);
    return () => { vivant = false; clearInterval(timer); };
  }, [multiAngle]);

  const trimmedName = sessionName.trim();
  const effectiveName = trimmedName || defaultName;

  // Base du nom de fichier. Champ vide → le nom par défaut tel quel. Champ saisi → le
  // même nettoyage que le nom par défaut subit (caractères interdits, espaces), PAS un
  // slug : la casse et les séparateurs sont ce qui rend une prise reconnaissable dans la
  // liste du Stockage.
  const sessionBase = trimmedName ? sanitizeSessionName(trimmedName) : defaultName;

  // En multi-angle, le programme est un fichier parmi d'autres : il porte le suffixe
  // FLUX-FINAL, là où les angles portent le nom de leur caméra. En enregistrement simple
  // il n'y a rien dont le distinguer, il garde donc son nom historique.
  const sessionFilename = multiAngle ? buildFinalMixFilename(sessionBase) : sessionBase;

  const angles = useMemo<RecordingAngle[]>(() => {
    if (!multiAngle) return [];
    return buildRecordingAngles(deckSources.filter(s => !disabledIds.has(s.id)), sessionBase);
  }, [multiAngle, deckSources, disabledIds, sessionBase]);

  // Fichiers écrits simultanément par la prise : le programme, plus un par angle coché.
  // C'est ce nombre qui divise le quota restant — un angle « à la source » compte comme
  // les autres, il consomme le même disque même s'il ne coûte rien au navigateur.
  const streamCount = 1 + angles.length;
  const remainingForRun = remainingSecondsForStreams(remainingSec, streamCount);

  // ── Ce que la prise va publier ────────────────────────────────────────────
  //
  // Un fait, pas un pronostic : autant de fois le plafond par angle qu'il y aura
  // d'angles PUBLIÉS. Un angle « à la source » ne publie rien — le manager pose
  // `record` sur un path MediaMTX existant — et ne compte donc pas.
  //
  // Il y avait ici, le 25/08/2026, une alerte comparant ce besoin au « montant
  // disponible » estimé par le navigateur. Elle a été RETIRÉE : cette estimation
  // suit ce qu'on émet et non la capacité du lien, elle vaut 471 kb/s sur une
  // connexion au repos comme 6 Mb/s sur une connexion chargée. Alerter là-dessus,
  // c'est faire peur au hasard. Le besoin, lui, est exact — on l'affiche seul, et
  // l'opérateur le confronte à ce qu'il sait de sa ligne.
  const anglesPublies = angles.filter(a => !a.existingPath).length;
  const besoinBps     = anglesPublies * ANGLE_MAX_BITRATE;

  // Un nom saisi qui ne contient aucune lettre ni chiffre (« --- ») ne donnerait aucune
  // base exploitable, donc un fichier sans nom côté microservice : on bloque le lancement.
  const nameInvalid  = !!trimmedName && slugifyRecordingName(trimmedName) === '';
  const noAngle      = multiAngle && angles.length === 0;
  // Compté sur les angles RETENUS : une source en souffrance mais décochée ne concerne
  // plus la prise, l'annoncer ne ferait qu'inquiéter.
  const anglesEnSouffrance = angles.filter(a => ailingSources[a.sourceId]).length;
  const startDisabled = nameInvalid || noAngle;

  const toggleSource = (id: string, on: boolean) => {
    setDisabledIds(prev => {
      const next = new Set(prev);
      if (on) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleStart = () => {
    if (startDisabled) return;
    onStart({ sessionName: effectiveName, filename: sessionFilename, angles });
  };

  // ── Clavier ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && !startDisabled) handleStart();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return ReactDOM.createPortal(
    <div style={S.overlay}>
      <div onClick={e => e.stopPropagation()} style={S.modal}>

        {/* ── Titre ──────────────────────────────────────────────────────── */}
        <div style={S.header}>
          <span style={S.title}>
            {multiAngle ? "Enregistrement d'une session Multi view" : "Enregistrement d'une session"}
          </span>
          <button onClick={onCancel} style={S.closeIcon} title="Fermer">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={S.body}>

          {/* ── Durée restante ───────────────────────────────────────────── */}
          {/* Les deux libellés partagent la même largeur fixe : la durée se retrouve
              ainsi exactement au-dessus de la zone de saisie du nom de session. */}
          <div style={S.row}>
            <span style={S.label}>Durée restante :</span>
            <span style={S.clock}>{formatSecondsAsHms(remainingForRun)}</span>
            {multiAngle && streamCount > 1 && (
              <span
                style={S.streamsNote}
                title={`Le quota est partagé entre les ${streamCount} fichiers écrits en même temps : le programme et ${angles.length} angle(s).`}
              >
                pour {streamCount} flux
              </span>
            )}
            <button onClick={onBuyStorage} style={S.storageBtn}>+ de stockage</button>
          </div>

          {/* ── Ce que la prise va coûter au lien montant ─────────────────── */}
          {multiAngle && anglesPublies > 0 && (
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
              margin: '2px 0 10px', padding: '7px 10px', borderRadius: 6,
              border: '1px solid rgba(148,163,184,0.25)', background: 'rgba(148,163,184,0.07)',
              fontFamily: 'monospace', fontSize: 12, color: 'rgba(226,232,240,0.9)',
            }}>
              <span>
                Cette prise publiera <b>{formatBps(besoinBps)}</b>
                <span style={{ fontSize: 10, opacity: 0.75 }}> ({anglesPublies} angle{anglesPublies > 1 ? 's' : ''} × {formatBps(ANGLE_MAX_BITRATE)})</span>
              </span>
              {angles.length > anglesPublies && (
                <span style={{ fontSize: 10, opacity: 0.7 }}>
                  {angles.length - anglesPublies} angle(s) enregistré(s) à la source, sans flux montant
                </span>
              )}
            </div>
          )}

          {/* ── Nom de la session ────────────────────────────────────────── */}
          <div style={{ ...S.row, alignItems: 'baseline' }}>
            <span style={S.label}>Nom de la session :</span>
            <input
              autoFocus
              type        = "text"
              value       = {sessionName}
              placeholder = {defaultName}
              onChange    = {e => setSessionName(e.target.value)}
              style       = {{ ...S.input, borderColor: nameInvalid ? '#f87171' : 'rgba(255,255,255,0.14)' }}
            />
          </div>

          {nameInvalid && (
            <div style={S.hintError}>
              Ce nom ne contient aucune lettre ni chiffre : impossible d'en tirer un nom de fichier.
            </div>
          )}

          {/* ── Sources à enregistrer (multi-angle) ──────────────────────── */}
          {multiAngle && (
            <div style={S.sourcesBlock}>
              <div style={S.sectionTitle}>Sources à enregistrer</div>

              {deckSources.length === 0 ? (
                <div style={S.empty}>
                  <VideoOff style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span>Aucune source dans le deck</span>
                </div>
              ) : (
                <div style={S.sourceList}>
                  {deckSources.map(source => {
                    const on     = !disabledIds.has(source.id);
                    const souci  = ailingSources[source.id];
                    return (
                      <div key={source.id} style={S.sourceRow}>
                        <Toggle
                          form             = "switch"
                          checked          = {on}
                          internalPosition = {false}
                          width            = "28px"
                          height           = "16px"
                          fillColorOn      = "#25C400"
                          fillColorOff     = "#334155"
                          onChange         = {isOn => toggleSource(source.id, isOn)}
                        />
                        <span style={{ ...S.sourceName, ...(souci ? S.sourceNameAiling : null) }}>{source.label}</span>
                        {souci && (
                          <span style={S.ailing} title={`Cette source ne livre aucune image (${souci}). Si elle ne se rétablit pas d'ici le lancement, son angle sera abandonné et la prise se fera sans elle.`}>
                            {souci}
                          </span>
                        )}
                        {source.mediamtxPath && (
                          <span style={S.atSource} title="Cette source vient déjà du serveur : elle y est enregistrée directement, sans rien renvoyer depuis la régie">
                            à la source
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {noAngle && deckSources.length > 0 && (
                <div style={S.hintError}>Sélectionnez au moins une source à enregistrer.</div>
              )}

              {/* Dit ce qui va se passer, pas seulement ce qui ne va pas : la prise
                  partira quand même, le programme prime toujours sur un angle. */}
              {anglesEnSouffrance > 0 && (
                <div style={S.hintWarn}>
                  {anglesEnSouffrance === 1
                    ? "1 source cochée ne livre aucune image : son angle sera abandonné et la prise se fera sans elle."
                    : `${anglesEnSouffrance} sources cochées ne livrent aucune image : leurs angles seront abandonnés et la prise se fera sans elles.`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Boutons ────────────────────────────────────────────────────── */}
        <div style={S.footer}>
          <button onClick={onCancel} style={S.cancelBtn}>Annuler</button>
          <button
            onClick  = {handleStart}
            disabled = {startDisabled}
            style    = {{
              ...S.startBtn,
              opacity: startDisabled ? 0.45 : 1,
              cursor : startDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Circle style={{ width: 15, height: 15, flexShrink: 0 }} />
            Lancer l'enregistrement
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  overlay: {
    position      : 'fixed',
    inset         : 0,
    zIndex        : 9999,
    background    : 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    display       : 'flex',
    alignItems    : 'center',
    justifyContent: 'center',
    padding       : 24,
  } as React.CSSProperties,

  modal: {
    background   : 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    border       : '1px solid #1e293b',
    borderRadius : 14,
    width        : '100%',
    maxWidth     : 560,
    maxHeight    : '88vh',
    boxShadow    : '0 24px 64px rgba(0,0,0,0.6)',
    overflow     : 'hidden',
    display      : 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,

  header: {
    position    : 'relative',
    padding     : '16px 44px',
    borderBottom: '1px solid #1e293b',
    flexShrink  : 0,
    textAlign   : 'center',
  } as React.CSSProperties,

  title: {
    fontSize  : 16,
    fontWeight: 700,
    color     : '#ffffff',
  } as React.CSSProperties,

  closeIcon: {
    position    : 'absolute',
    top         : 12,
    right       : 12,
    background  : 'none',
    border      : 'none',
    cursor      : 'pointer',
    color       : '#ffffff',
    display     : 'flex',
    padding     : 4,
    borderRadius: 6,
  } as React.CSSProperties,

  body: {
    padding      : '18px 20px',
    display      : 'flex',
    flexDirection: 'column',
    gap          : 10,
    overflowY    : 'auto',
  } as React.CSSProperties,

  row: {
    display   : 'flex',
    alignItems: 'center',
    gap       : 10,
  } as React.CSSProperties,

  label: {
    fontSize  : 14,
    fontWeight: 600,
    color     : '#ffffff',
    flexShrink: 0,
    width     : 148,
  } as React.CSSProperties,

  clock: {
    fontFamily        : 'monospace',
    fontSize          : 15,
    fontWeight        : 700,
    color             : '#ffffff',
    fontVariantNumeric: 'tabular-nums',
    flexShrink        : 0,
  } as React.CSSProperties,

  streamsNote: {
    fontSize  : 12,
    fontWeight: 600,
    color     : '#facc15',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  } as React.CSSProperties,

  storageBtn: {
    marginLeft  : 'auto',
    background  : 'rgba(255,255,255,0.06)',
    border      : '1px solid rgba(255,255,255,0.14)',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 12,
    fontWeight  : 700,
    padding     : '6px 12px',
    cursor      : 'pointer',
    whiteSpace  : 'nowrap',
  } as React.CSSProperties,

  input: {
    flex        : 1,
    minWidth    : 0,
    background  : 'rgba(0,0,0,0.3)',
    border      : '1px solid rgba(255,255,255,0.14)',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 14,
    padding     : '8px 10px',
    outline     : 'none',
  } as React.CSSProperties,

  hintError: {
    fontSize: 12,
    color   : '#fca5a5',
  } as React.CSSProperties,

  hintWarn: {
    fontSize : 12,
    color    : '#fbbf24',
    marginTop: 6,
  } as React.CSSProperties,

  // Même gabarit que le badge « à la source », en ambre : c'est un avertissement, pas
  // une erreur — la source peut se rétablir avant le lancement.
  ailing: {
    marginLeft   : 'auto',
    fontSize     : 10,
    fontWeight   : 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color        : '#fbbf24',
    border       : '1px solid rgba(251,191,36,0.35)',
    borderRadius : 5,
    padding      : '2px 6px',
    whiteSpace   : 'nowrap',
  } as React.CSSProperties,

  sourceNameAiling: {
    opacity: 0.55,
  } as React.CSSProperties,

  sourcesBlock: {
    marginTop  : 6,
    paddingTop : 12,
    borderTop  : '1px solid rgba(255,255,255,0.08)',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize     : 11,
    fontWeight   : 700,
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
    color        : '#ffffff',
    marginBottom : 8,
  } as React.CSSProperties,

  sourceList: {
    display      : 'flex',
    flexDirection: 'column',
    maxHeight    : 260,
    overflowY    : 'auto',
  } as React.CSSProperties,

  sourceRow: {
    display     : 'flex',
    alignItems  : 'center',
    gap         : 18,
    padding     : '6px 2px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  } as React.CSSProperties,

  sourceName: {
    fontSize    : 14,
    color       : '#ffffff',
    flex        : 1,
    minWidth    : 0,
    overflow    : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace  : 'nowrap',
  } as React.CSSProperties,

  atSource: {
    marginLeft  : 'auto',
    fontSize    : 10,
    fontWeight  : 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color       : '#4ade80',
    border      : '1px solid rgba(74,222,128,0.35)',
    borderRadius: 5,
    padding     : '2px 6px',
    whiteSpace  : 'nowrap',
    flexShrink  : 0,
  } as React.CSSProperties,

  empty: {
    display   : 'flex',
    alignItems: 'center',
    gap       : 8,
    padding   : '10px 0',
    fontSize  : 13,
    color     : '#94a3b8',
  } as React.CSSProperties,

  footer: {
    padding       : '12px 20px',
    borderTop     : '1px solid #0f172a',
    background    : 'rgba(0,0,0,0.2)',
    display       : 'flex',
    justifyContent: 'flex-end',
    gap           : 10,
    flexShrink    : 0,
  } as React.CSSProperties,

  cancelBtn: {
    background  : 'rgba(255,255,255,0.05)',
    border      : '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 13,
    fontWeight  : 600,
    padding     : '8px 18px',
    cursor      : 'pointer',
  } as React.CSSProperties,

  startBtn: {
    background  : '#dc2626',
    border      : '1px solid #dc2626',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 13,
    fontWeight  : 700,
    padding     : '8px 18px',
    display     : 'flex',
    alignItems  : 'center',
    gap         : 7,
  } as React.CSSProperties,
};

export default RecordingSessionModal;
